import {
  FailedDestinationEntityRecord,
  IDestinationEntityJobRunOptions,
  ITranslationMap,
  SkippedDestinationEntityRecord,
  SourceEntityRecordStatus,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { Contact } from '@principle-theorem/principle-core';
import {
  IReferralSourceConfiguration,
  isContactDetails,
} from '@principle-theorem/principle-core/interfaces';
import {
  snapshotCombineLatest,
  WithPrimaryKey,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  BaseContactDestinationEntity,
  IContactJobData,
  IContactMigrationData,
} from '../../../destination/entities/contact';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import {
  ContactsSourceEntity,
  ExactContact,
  type IExactContact,
} from '../../source/entities/contacts';
import { ExactContactMappingHandler } from '../mappings/contact-to-referral-source';

interface IJobData extends IContactJobData<ExactContact> {
  contactsMappings: WithPrimaryKey<
    ITranslationMap<IReferralSourceConfiguration>
  >[];
}

export class ContactDestinationEntity extends BaseContactDestinationEntity<
  ExactContact,
  IContactJobData<ExactContact>
> {
  contactSourceEntity = new ContactsSourceEntity();

  customMappings = {
    contactToReferrals: new ExactContactMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IJobData[]> {
    const contactsMappings$ =
      this.customMappings.contactToReferrals.getRecords(translationMap);

    return snapshotCombineLatest([
      PracticeMigration.brand$(migration),
      contactsMappings$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.contactSourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourceContacts, [brand, contactsMappings]]) =>
        sourceContacts.map((sourceContact) => ({
          sourceContact,
          brand,
          contactsMappings,
        }))
      )
    );
  }

  buildMigrationData(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    data: IJobData
  ):
    | IContactMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
    | (IDestinationEntityRecord & SkippedDestinationEntityRecord) {
    if (data.sourceContact.record.status === SourceEntityRecordStatus.Invalid) {
      return this._buildErrorResponse(
        data.sourceContact,
        'Source contact is invalid'
      );
    }

    const contactData = data.sourceContact.data;
    const sourceContactId =
      this.contactSourceEntity.getSourceRecordId(contactData);

    const isMappedToReferralSource = data.contactsMappings.some(
      (referralSource) =>
        referralSource.sourceIdentifier === sourceContactId.toString() &&
        !!referralSource.destinationIdentifier
    );

    const contactDetails = {
      name: contactData.name,
      email: contactData.email ?? undefined,
      phone: contactData.workphone ?? contactData.homephone ?? undefined,
      address: getContactAddress(contactData),
      mobileNumber: contactData.mobile ?? undefined,
    };

    if (isMappedToReferralSource || !isContactDetails(contactDetails)) {
      return this.buildSkippedResponse(data);
    }

    return {
      sourceContactId,
      contact: Contact.init(contactDetails),
    };
  }
}

function getContactAddress(contact: IExactContact): string | undefined {
  const parts = compact([
    contact.address_1,
    contact.address_2,
    contact.city,
    contact.suburb,
    contact.state,
    contact.post_code,
  ]);
  return parts.length > 0 ? parts.join(' ') : undefined;
}
